// Pure deadline/urgency logic — the single source of truth for how the UI
// colors, sorts, and prioritizes dates. No Vue, no store imports: unit-testable.

import type { Stage } from '@/lib/types'

export type Urgency = 'past' | 'today' | 'urgent' | 'soon' | 'normal'

export type DateInput = string | Date | null | undefined

function toDate(input: DateInput): Date | null {
  if (input === null || input === undefined || input === '') return null
  const d = input instanceof Date ? input : new Date(input)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Midnight (local) for a date — calendar-day arithmetic base. */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Whole calendar days from today (local) until `date`.
 * 0 = due today, negative = past, null = no/invalid date.
 */
export function daysLeft(date: DateInput, now: Date = new Date()): number | null {
  const d = toDate(date)
  if (!d) return null
  return Math.round((startOfDay(d).getTime() - startOfDay(now).getTime()) / MS_PER_DAY)
}

/**
 * Urgency bucket for a date:
 *  past (< today) | today | urgent (≤7d) | soon (≤14d) | normal (>14d or none).
 */
export function urgencyFor(date: DateInput, now: Date = new Date()): Urgency {
  const days = daysLeft(date, now)
  if (days === null) return 'normal'
  if (days < 0) return 'past'
  if (days === 0) return 'today'
  if (days <= 7) return 'urgent'
  if (days <= 14) return 'soon'
  return 'normal'
}

const DATE_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})
const TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
})

/** "Mar 14, 2026" — appends the local time ("· 5:00 PM") when non-midnight. */
export function formatDeadline(date: DateInput): string {
  const d = toDate(date)
  if (!d) return '—'
  const base = DATE_FMT.format(d)
  if (d.getHours() === 0 && d.getMinutes() === 0) return base
  return `${base} · ${TIME_FMT.format(d)}`
}

/** Short countdown label for pills: "3d", "Today", "Overdue 2d", "—". */
export function countdownLabel(date: DateInput, now: Date = new Date()): string {
  const days = daysLeft(date, now)
  if (days === null) return '—'
  if (days < 0) return `Overdue ${-days}d`
  if (days === 0) return 'Today'
  return `${days}d`
}

/** Minimum fields atRisk needs — both Pursuit list rows and detail satisfy it. */
export interface AtRiskInput {
  stage: string
  effective_deadline: string | null
  stages?: Stage[]
}

/** Stage keys that represent terminal/closed work and never count as remaining. */
const TERMINAL_STAGE_KEYS = new Set(['done', 'closed'])

/**
 * Count of template stages still ahead of the current one (template order),
 * excluding terminal done/closed stages. Unknown stage → every non-terminal
 * stage counts (conservative).
 */
export function stagesRemaining(stage: string, stages: Stage[] | undefined): number {
  if (!stages || stages.length === 0) return 0
  const idx = stages.findIndex((s) => s.key === stage)
  const after = idx === -1 ? stages : stages.slice(idx + 1)
  return after.filter((s) => !TERMINAL_STAGE_KEYS.has(s.key)).length
}

/**
 * True when the remaining work plausibly does not fit in the remaining time:
 * daysRemaining < stagesRemaining × 1.5 effort-days + 1 buffer day.
 * No deadline → never at risk.
 */
export function atRisk(pursuit: AtRiskInput, stages?: Stage[], now: Date = new Date()): boolean {
  const days = daysLeft(pursuit.effective_deadline, now)
  if (days === null) return false
  const remaining = stagesRemaining(pursuit.stage, stages ?? pursuit.stages)
  return days < remaining * 1.5 + 1
}
